import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "../api";
import {
  clearAuthUserSnapshot,
  clearAuthSessionHint,
  getAuthUserSnapshot,
  hasAuthSessionHint,
  setAuthUserSnapshot,
  setAuthSessionHint,
} from "../utils/authSession";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getAuthUserSnapshot());
  const [loading, setLoading] = useState(true);
  const latestUserRef = useRef(user);
  const inFlightRef = useRef(null);
  const lastResolvedAtRef = useRef(0);

  useEffect(() => {
    latestUserRef.current = user;
  }, [user]);

  const refreshAuth = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && !hasAuthSessionHint()) {
      setUser(null);
      setLoading(false);
      return null;
    }

    if (!force && latestUserRef.current && now - lastResolvedAtRef.current < 15000) {
      setLoading(false);
      return latestUserRef.current;
    }

    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    const request = (async () => {
      try {
        const { data } = await api.get("/auth/me");
        const nextUser = data?.user || null;
        setUser(nextUser);
        setAuthSessionHint();
        if (nextUser) {
          setAuthUserSnapshot(nextUser);
          lastResolvedAtRef.current = Date.now();
        }
        return nextUser;
      } catch (err) {
        const status = Number(err?.response?.status || 0);
        if (status === 401) {
          clearAuthSessionHint();
          clearAuthUserSnapshot();
          setUser(null);
          lastResolvedAtRef.current = 0;
          return null;
        }
        const fallbackUser = latestUserRef.current || getAuthUserSnapshot();
        if (fallbackUser) {
          setUser(fallbackUser);
          lastResolvedAtRef.current = Date.now();
        }
        return fallbackUser;
      } finally {
        inFlightRef.current = null;
        setLoading(false);
      }
    })();

    inFlightRef.current = request;
    return request;
  }, []);

  useEffect(() => {
    void refreshAuth();
  }, [refreshAuth]);

  useEffect(() => {
    const expireSession = () => {
      clearAuthSessionHint();
      clearAuthUserSnapshot();
      setUser(null);
      setLoading(false);
    };
    window.addEventListener("takofy:session-expired", expireSession);
    return () => window.removeEventListener("takofy:session-expired", expireSession);
  }, []);

  useEffect(() => {
    const syncAcrossWindows = (event) => {
      if (event.key !== "takofy_has_session_hint" && event.key !== "takofy_auth_user_snapshot") return;
      if (!hasAuthSessionHint()) {
        clearAuthUserSnapshot();
        setUser(null);
        setLoading(false);
        return;
      }
      void refreshAuth(true);
    };
    window.addEventListener("storage", syncAcrossWindows);
    return () => window.removeEventListener("storage", syncAcrossWindows);
  }, [refreshAuth]);

  const markLoggedIn = (nextUser = null) => {
    setAuthSessionHint();
    if (nextUser) {
      setAuthUserSnapshot(nextUser);
      setUser(nextUser);
    }
  };

  const logoutLocal = () => {
    clearAuthSessionHint();
    clearAuthUserSnapshot();
    setUser(null);
    setLoading(false);
  };

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      markLoggedIn,
      logoutLocal,
      refreshAuth,
      setUser,
    }),
    [user, loading, refreshAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return value;
}
